import type { ReactNode } from 'react';

type PageShellProps = {
  title: ReactNode;
  /** One or two sentences on what the screen is for and what it replaced. */
  subtitle?: ReactNode;
  /** Primary actions, rendered on the title row (Refresh, Create …). */
  actions?: ReactNode;
  /** A standing caveat about the screen, e.g. "tenants live in git". */
  notice?: ReactNode;
  /** Filters and search, pinned to the top of the panel. */
  toolbar?: ReactNode;
  /** The panel body — normally a DataTable, which owns the only scrollbar. */
  children: ReactNode;
};

/**
 * The frame every screen uses: title block, optional notice and toolbar, and a
 * panel that fills the rest of the viewport exactly.
 *
 * Everything here is `flex: none` apart from the panel, so the header and the
 * filters stay where the eye left them and the rows scroll underneath. Nothing
 * in the app scrolls the window itself — see `body { overflow: hidden }`.
 */
export default function PageShell({
  title,
  subtitle,
  actions,
  notice,
  toolbar,
  children,
}: PageShellProps) {
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <p className="page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div>{actions}</div>}
      </div>

      {notice && <div className="page-notice">{notice}</div>}

      <div className="panel">
        {toolbar && <div className="panel-toolbar">{toolbar}</div>}
        <div className="panel-body">{children}</div>
      </div>
    </div>
  );
}
